/**
 * query.ts — TanStack Query client + typed query key factories.
 * Per frontend-conventions.md §8: keys are typed via factories, never stringly arrays.
 */

import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30s; tune per query as needed
      refetchOnWindowFocus: false, // see frontend-conventions.md §8 — re-enable when stale-data UI exists
      retry: (failureCount, error) => {
        // Don't retry on 4xx; do retry on 5xx and network errors (up to 3 times).
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 3;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Query key factories. Each entity gets a small namespace.
 * Tests assert key shape; lib/api.ts and feature hooks consume these.
 */
export const queryKeys = {
  // Auth / current user
  auth: {
    me: () => ['auth', 'me'] as const,
  },
  // Members — full shapes added as features land
  members: {
    all: () => ['members'] as const,
    list: (tenantId: string) => ['members', 'list', tenantId] as const,
    detail: (memberId: string) => ['members', 'detail', memberId] as const,
  },
  // Jobs
  jobs: {
    all: () => ['jobs'] as const,
    list: (tenantId: string) => ['jobs', 'list', tenantId] as const,
    detail: (jobId: string) => ['jobs', 'detail', jobId] as const,
  },
  // Templates
  templates: {
    all: () => ['templates'] as const,
    list: (tenantId: string) => ['templates', 'list', tenantId] as const,
    detail: (templateId: string) => ['templates', 'detail', templateId] as const,
  },
} as const;
